import { useMemo, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import ReusableTable from "../../../../components/ReusableTable/ReusableTable";
import ResourceNameCell from "../../../Checks/components/common/ResourceNameCell";
import ResourceStatusCell from "../../../Checks/components/common/ResourceStatusCell";
import EvidenceTableContainer from "../common/EvidenceTableContainer";
import StatusFilterPopover from "../common/StatusFilterPopover";

const GenericEvidenceTab = ({ checkDetails, defaultStatusFilter = [] }) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(defaultStatusFilter);

  // Map resources for display
  const resourcesList = useMemo(() => {
    if (!checkDetails?.resources) return [];

    return checkDetails.resources.map((res) => ({
      id: res.id || res.resource_id,
      resourceName: res.resource_name || res.resource_id || "-",
      status: res.status || res.check_status || "Unknown",
      reason: res.status_reason || "-",
    }));
  }, [checkDetails]);

  const filteredResources = useMemo(() => {
    let filtered = resourcesList;

    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter((item) =>
        item?.resourceName?.toLowerCase().includes(query) ||
        item?.status?.toLowerCase().includes(query) ||
        item?.reason?.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (statusFilter.length > 0) {
      filtered = filtered.filter((item) =>
        statusFilter.includes((item.status || "").toLowerCase())
      );
    }

    return filtered;
  }, [resourcesList, searchTerm, statusFilter]);

  const columns = [
    { label: "Resource Name", field: "resourceName", align: "left", width: "15%" },
    {
      label: (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          Status
          <StatusFilterPopover
            onFilterChange={setStatusFilter}
            activeFilters={statusFilter}
          />
        </Box>
      ),
      field: "status",
      align: "center",
      width: "15%"
    },
    { label: "Reason", field: "reason", align: "left", width: "60%" },
  ];

  const renderCell = (row, column) => {
    switch (column.field) {
      case "resourceName":
        return (
          <Box sx={{ minWidth: 0, overflow: "hidden" }}>
            <ResourceNameCell value={row.resourceName} showCopy={false} />
          </Box>
        );
      case "status":
        return <ResourceStatusCell status={row.status} />;
      case "reason":
        return (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              fontSize: "0.875rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              lineHeight: 1.5,
              color: theme.customColors.text.strong,
            }}
          >
            {row.reason || "-"}
          </Typography>
        );
      default:
        return (
          <Typography
            variant="body2"
            sx={{
              color: theme.customColors.text.strong,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row[column.field] || "-"}
          </Typography>
        );
    }
  };

  // If no resources, show message
  if (!resourcesList || resourcesList.length === 0) {
    return (
      <Box
        sx={{
          borderRadius: "12px",
          border: `1px solid ${theme.customColors.border.main}`,
          backgroundColor: theme.customColors.background.main,
          boxShadow: theme.customColors.shadow.main,
          p: 4,
          textAlign: "center",
        }}
      >
        <Typography variant="body1" fontWeight={600} sx={{ mb: 2 }}>
          No resources found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          There are no resources available for this check.
        </Typography>
      </Box>
    );
  }

  return (
    <EvidenceTableContainer
      title="Resources"
      searchTerm={searchTerm}
      onSearchChange={(e) => setSearchTerm(e.target.value)}
      clearFilters={() => {
        setSearchTerm("");
        setStatusFilter([]);
      }}
      filteredCount={filteredResources.length}
      countLabel={`${filteredResources.length} ${filteredResources.length === 1 ? "resource" : "resources"} found`}
      tableExpanded={true}
      onToggleExpand={() => {}}
      showExpandCollapse={false}
    >
      <ReusableTable
        columns={columns}
        data={filteredResources}
        renderCell={renderCell}
        showPagination={true}
        stickyHeader
        maxHeight="500px"
        rowsPerPageOptions={[25, 50, 100]}
      />
    </EvidenceTableContainer>
  );
};

export default GenericEvidenceTab;
